import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Copy, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Design } from '@/api/designs';
import { Button, ConfirmDialog } from '@/components/ui';
import { useDeleteDesign, useDuplicateDesign } from '@/hooks/useDesigns';
import { useDesignStore } from '@/stores';

export interface DesignRowActionsProps {
  design: Design;
}

/**
 * Open, duplicate and delete for one row of the overview (plan U10).
 *
 * The same three actions the cards used to carry, lifted out so the table and
 * any later surface share one behaviour — deleting still asks first and warns
 * when the listing is live. Opening lands on the products step filtered to
 * the listing's own products: reviewing a listing is the common case, and
 * the design step is one click away.
 */
export function DesignRowActions({ design }: DesignRowActionsProps) {
  const { t } = useTranslation();
  const deleteDesign = useDeleteDesign();
  const duplicateDesign = useDuplicateDesign();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const name = design.name || t('seller.designs.untitled');

  const handleDuplicate = () => {
    duplicateDesign.mutate(design.id, {
      onSuccess: () => toast.success(t('seller.designs.toasts.duplicated')),
      onError: () => toast.error(t('seller.designs.toasts.duplicateFailed')),
    });
  };

  const handleConfirmDelete = () => {
    deleteDesign.mutate(design.id, {
      onSuccess: () => {
        // The creator's "back to design" link must not point at a design
        // that no longer exists.
        const designStore = useDesignStore.getState();
        if (designStore.designId === design.id) {
          designStore.clearDesign();
        }
        toast.success(t('seller.designs.toasts.deleted'));
        setConfirmOpen(false);
      },
      onError: () => toast.error(t('seller.designs.toasts.deleteFailed')),
    });
  };

  return (
    <div className="flex items-center justify-end gap-1.5">
      <Button asChild variant="outline" size="sm">
        <Link to={`/creator/${design.id}?step=products&filter=active`}>
          {t('seller.designs.actions.open')}
        </Link>
      </Button>
      <Button
        variant="outline"
        size="icon-lg"
        onClick={handleDuplicate}
        loading={duplicateDesign.isPending}
        aria-label={t('seller.designs.actions.duplicate')}
        title={t('seller.designs.actions.duplicate')}
      >
        <Copy className="h-4 w-4" aria-hidden="true" />
      </Button>
      <Button
        variant="outline"
        size="icon-lg"
        onClick={() => setConfirmOpen(true)}
        aria-label={t('seller.designs.actions.delete')}
        title={t('seller.designs.actions.delete')}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        // Escape and the overlay must not close the dialog while the request
        // runs; the loading state alone does not stop Radix.
        onOpenChange={(open) => {
          if (!deleteDesign.isPending) setConfirmOpen(open);
        }}
        title={t('seller.designs.delete.title')}
        description={t(
          design.status === 'ACTIVE'
            ? 'seller.designs.delete.textPublished'
            : 'seller.designs.delete.text',
          { name }
        )}
        confirmLabel={t('seller.designs.delete.confirm')}
        cancelLabel={t('seller.designs.delete.cancel')}
        destructive
        loading={deleteDesign.isPending}
        loadingLabel={t('common.loading')}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
